import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navigation.css";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentView, setCurrentView] = useState('main'); // 'main' or 'subpages'
  const [selectedNavItem, setSelectedNavItem] = useState(null);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  // Desktop dropdowns rely on CSS :hover for mouse users, but touch/no-hover
  // devices (touch laptops, some tablets) need an explicit tap-to-open toggle.
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdownIndex(null);
      }
    };
    document.addEventListener("pointerdown", handleOutsideClick);
    return () => document.removeEventListener("pointerdown", handleOutsideClick);
  }, []);

  const toggleDropdown = (index) => {
    setOpenDropdownIndex(prev => (prev === index ? null : index));
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setCurrentView('main');
    setSelectedNavItem(null);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };


  const showSubpages = (navItem, index) => {
    setSelectedNavItem(navItem);
    setCurrentView('subpages');
  };

  const goBackToMain = () => {
    setCurrentView('main');
    setSelectedNavItem(null);
  };

  const handleClick = (path) => {
    closeMenu();
    setCurrentView('main');
    setSelectedNavItem(null);
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };


  const navItems = [
    {
      title: "About",
      items: [
        { name: "Our Mission", path: "/mission" },
        { name: "Our Mentors", path: "/mentors" },
        { name: "Our Ethos", path: "/ethos" }
      ]
    },
    {
      title: "Services",
      items: [
        { name: "Tuition", path: "/tuition" },
        { name: "University Application Support", path: "/personal-statement" },
        { name: "Corporate Application Support", path: "/cv-support" }
      ]
    },
    {
      title: "Insights",
      items: [
        { name: "Biweekly Blog", path: "/blog" },
        { name: "Our Socials", path: "/socials" }
      ]
    },
    {
      title: "Support",
      items: [
        { name: "Get Involved", path: "/get-involved" },
        { name: "FAQ", path: "/faq" }
      ]
    }
  ];

  return (
    <nav className="navigation" ref={navRef}>
      <div className="nav-container">
        <Link to="/" className="nav-logo" onClick={() => handleClick("/")}>
          <div className="logo-container">
            <img src="/logo.svg" alt="MCA Logo" className="nav-logo-image" />
            <div className="logo-text-container">
              <span className="logo-text">MCA</span>
              <span className="logo-subtitle">Muslim Corporate Academy</span>
            </div>
          </div>
        </Link>

        <div className={`nav-links ${isMenuOpen ? "nav-links-open" : ""}`}>
          {/* Desktop Navigation - Hover-based */}
          <div className="desktop-nav">
            <Link to="/" className="nav-home-link" onClick={() => handleClick("/")}>
              Home
            </Link>
            {navItems.map((item, index) => (
              <div key={index} className="nav-item">
                <span
                  className="nav-link"
                  onClick={() => toggleDropdown(index)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={openDropdownIndex === index}
                >
                  {item.title}
                </span>
                <div className={`dropdown-menu ${openDropdownIndex === index ? "dropdown-menu-open" : ""}`}>
                  {item.items.map((subItem, subIndex) => (
                    <Link
                      key={subIndex}
                      to={subItem.path}
                      className="dropdown-item"
                      onClick={() => {
                        setOpenDropdownIndex(null);
                        handleClick(subItem.path);
                      }}
                    >
                      {subItem.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Navigation - Slide-in system */}
          <div className={`mobile-nav-content ${currentView === 'subpages' ? 'mobile-nav-content-subpages' : 'mobile-nav-content-main'}`}>
            
            {/* Main Navigation View */}
            <div className={`mobile-nav-main-view ${currentView === 'main' ? 'mobile-nav-view-active' : 'mobile-nav-view-hidden'}`}>
              <Link to="/" className="mobile-nav-home-link" onClick={() => handleClick("/")}>
                Home
              </Link>
              {navItems.map((item, index) => (
                <button 
                  key={index}
                  className="mobile-nav-main-button"
                  onClick={() => showSubpages(item, index)}
                >
                  <span>{item.title}</span>
                  <span className="mobile-nav-arrow">→</span>
                </button>
              ))}
            </div>

            {/* Subpages Navigation View */}
            <div className={`mobile-nav-subpages-view ${currentView === 'subpages' ? 'mobile-nav-view-active' : 'mobile-nav-view-hidden'}`}>
              <button className="mobile-nav-back-button" onClick={goBackToMain}>
                <span className="mobile-nav-back-arrow">←</span>
                <span>Back</span>
              </button>
              <div className="mobile-nav-subpages-title">
                {selectedNavItem?.title}
              </div>
              {selectedNavItem?.items.map((subItem, subIndex) => (
                <Link
                  key={subIndex}
                  to={subItem.path}
                  className="mobile-nav-subpage-link"
                  onClick={() => handleClick(subItem.path)}
                >
                  {subItem.name}
                </Link>
              ))}
            </div>

          </div>
        </div>

        <button className="nav-toggle" onClick={toggleMenu}>
          <span className={`hamburger ${isMenuOpen ? "hamburger-open" : ""}`}></span>
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
